import { ImageName } from "@mcleod/images";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import {
    Button, Checkbox, Component, DataSource, DataSourceMode, Image, Label, Layout, Panel, Snackbar, StarRating, Table, TableRow,
    TextboxProps, Toast
} from "@mcleod/components";
import { Alignment, Api, DisplayType, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { IntBillCommentsQuickInfo } from "./IntBillCommentsQuickInfo";
import { InvoiceProcessingChargesQuickInfo } from "./InvoiceProcessingChargesQuickInfo";
import { OnHoldInvoiceReasonCodeQuickInfo } from "./OnHoldInvoiceReasonCodeQuickInfo";
import { BillingCommentsQuickInfo } from "./BillingCommentsQuickInfo";
import { ExtBillCommentsQuickInfo } from "./ExtBillCommentsQuickInfo";
import { FilterPanel, SecondaryFilter, SecondaryFilterProps } from "@mcleod/dispatch/src/planning/FilterPanel";
import { InvoiceProcessingTableType } from "./InvoiceProcessingTableType";
import { LMEUserSettings } from "@mcleod/general";
import { DrsPayee } from "@mcleod/settlement/src/DrsPayee";
import { OrderTabView } from "@mcleod/dispatch";

export function calculateRefreshDelay(recordCount?: number): number {
    // Constants for refresh delays
    const SINGLE_RECORD_DELAY_MS = 500;
    const SMALL_BATCH_DELAY_MS = 1000;  // 2-3 records
    const MEDIUM_BATCH_DELAY_MS = 1500; // 4-5 records
    const LARGE_BATCH_DELAY_MS = 2000;  // 6+ records

    // Use progressively longer delays for multi-record operations
    let delay = SINGLE_RECORD_DELAY_MS; // Base delay for single record

    if (recordCount && recordCount > 1) {
        // For multi-record operations, use longer delays:
        // 2-3 records: 1000ms, 4-5 records: 1500ms, 6+ records: 2000ms
        if (recordCount <= 3) {
            delay = SMALL_BATCH_DELAY_MS;
        } else if (recordCount <= 5) {
            delay = MEDIUM_BATCH_DELAY_MS;
        } else {
            delay = LARGE_BATCH_DELAY_MS;
        }
    }

    return delay;
}

/**
 * Schedules a refresh operation with appropriate delay based on record count
 * @param refreshCallback - Function to call after the delay
 * @param recordCount - Number of records being processed (optional)
 * @param loggerName - Name for the logger instance
 */
export function scheduleRefreshWithDelay(
    refreshCallback: () => void,
    recordCount?: number,
    loggerName: string = "InvoiceProcessingUtil"
): void {
    const delay = calculateRefreshDelay(recordCount);
    const log = LogManager.getLogger(loggerName);

    log.debug(`Starting refresh with ${delay}ms delay${recordCount ? ` for ${recordCount} record operation` : ''}`);
    setTimeout(refreshCallback, delay);
}

export interface InvoiceProcessingTable extends Layout {
    filterPanel: FilterPanel;
    tableProcessing: Table;
    type: InvoiceProcessingTableType;
}

export function setWeight(modelRow: ModelRow): string {
    let weight = modelRow.get("weight");
    if (!weight) {
        weight = "0"
    }
    const weightUOM = modelRow.get("weight_uom");

    return weightUOM ? `${weight} ${weightUOM}` : `${weight}`;
}

export function setPieces(modelRow: ModelRow): string {
    let pieces = modelRow.get("pieces");
    if (!pieces) {
        pieces = "0"
    }
    return `${pieces} PCS`;
}


export function setCommentsInfo(tableRow: TableRow, orderId: string, customerId: string): void {
    Api.search("lme/ar/billing-comments", { order_id: orderId, customer_id: customerId }).then((response) => {
        const rowCount = response.data[0]["number_of_comments"];
        const isRowCountNotZero = rowCount !== 0;
        const commentCount = tableRow.findComponentById("labelCommentsNumber") as Label;
        const commentIcon = tableRow.findComponentById("imageComments") as Image;
        if (commentCount) {
            commentCount.visible = isRowCountNotZero ? true : false;
            commentCount.text = isRowCountNotZero ? "(" + rowCount + ")" : commentCount.text;
        }
        if (commentIcon) {
            commentIcon.visible = isRowCountNotZero ? true : false;
        }
    });
}


export async function updateExceptionsPanel(tableRow: any, modelRow: any, printed: boolean) {
    const exceptionPanel = tableRow.findComponentById("panelException") as Panel;
    const exceptions = modelRow.get("invoice_exceptions");
    const numberOfExceptions = exceptions?.length ?? 0;
    if (exceptionPanel) {
        exceptionPanel.visible = numberOfExceptions > 0;
    }

    await Api.search("lme/ar/imaging-documents-lookup", { row_id: modelRow.get("order_id"), row_type: "O" }).then((response) => {
        const labelMissingImage = exceptionPanel?.findComponentById("labelExceptionText") as Label;
        const imageMissingImage = exceptionPanel?.findComponentById("imageException") as Image;

        if (Array.isArray(response.data) && response.data.length > 0 && response.data[0].imaging_permitted &&
            response.data[0].required_image_missing_count > 0) {
            if (exceptionPanel) {
                exceptionPanel.visible = true;
            }
            const missingImageCount = response.data[0].required_image_missing_count;
            const totalExceptionCount = numberOfExceptions + missingImageCount + (printed ? 1 : 0);
            const images = response.data[0].images;
            const missingImages = images.filter(image => image.is_missing === "Y");
            let totalIndex = -1;
            if (labelMissingImage) {
                labelMissingImage.visible = true;
            }
            if (imageMissingImage) {
                imageMissingImage.visible = true;
            }
            if (totalExceptionCount > 1) {
                if (labelMissingImage) {
                    labelMissingImage.text = `(${totalExceptionCount})`;
                }
                const textboxPropsArray: Partial<TextboxProps>[] = [totalExceptionCount];
                missingImages.forEach((image, index) => {
                    textboxPropsArray[index] = {
                        caption: "Missing Image",
                        text: image.description,
                        displayType: DisplayType.STRING,
                        rowBreak: true,
                        fillRow: true
                    }
                    totalIndex = index;
                });

                if (printed) {
                    textboxPropsArray[totalIndex + 1] = {
                        caption: "Status",
                        text: "Invoice has been printed",
                        displayType: DisplayType.STRING,
                        rowBreak: true,
                        fillRow: true
                    }
                    totalIndex++;
                }

                if (numberOfExceptions > 0) {
                    for (let i = 0; i < exceptions.length; i++) {
                        if (imageMissingImage) {
                            imageMissingImage.name = ImageName.circleErrorSolid;
                            imageMissingImage.color = "error";
                        }
                        const currException = exceptions[i];
                        textboxPropsArray[totalIndex + 1] = {
                            caption: "Invoice Exception",
                            text: currException["exception_description"],
                            displayType: DisplayType.STRING,
                            rowBreak: true,
                            fillRow: true
                        }
                        totalIndex++;
                    }
                } else {
                    if (imageMissingImage) {
                        imageMissingImage.color = "warning";
                    }
                }

                CommonTooltips.setTooltipContentTextboxes(imageMissingImage, () => textboxPropsArray);

            } else if (missingImageCount === 1 && missingImages.length > 0) {
                if (imageMissingImage) {
                    imageMissingImage.color = "warning";
                }
                if (labelMissingImage) {
                    labelMissingImage.text = "Missing image: " + missingImages[0]?.description;
                }
            } else if (printed) {
                if (imageMissingImage) {
                    imageMissingImage.visible = true;
                    imageMissingImage.color = "primary";
                }
                if (labelMissingImage) {
                    labelMissingImage.text = "Invoice has been printed";
                }
            }
        } else if (numberOfExceptions > 0) {
            setExceptions(exceptionPanel, modelRow, printed);
        } else if (printed) {  //comment for check in
            if (exceptionPanel) {
                exceptionPanel.visible = true;
            }
            if (labelMissingImage) {
                labelMissingImage.text = "Invoice has been printed";
            }
            if (imageMissingImage) {
                imageMissingImage.visible = true;
                imageMissingImage.color = "primary";
            }
        } else {
            exceptionPanel.visible = false;
        }
        modelRow.set("has_exceptions", exceptionPanel.visible);
    });
}

export function setInvoiceStatusImage(tableRow: TableRow): void {
    const statusLabel = tableRow.findComponentById("labelBillingStatus") as Label;
    if (statusLabel) {
        if (statusLabel.text === "Pending Review") {
            statusLabel.imageName = ImageName.settlementProcess;
            statusLabel.imageColor = "warning";
        } else if (statusLabel.text === "Hold") {
            statusLabel.imageName = ImageName.onHold;
            statusLabel.imageColor = "error";
            statusLabel.text = "";
            statusLabel.paddingRight = 0;
        } else if (statusLabel.text === "Ready to Print") {
            statusLabel.imageName = ImageName.readyToPrint;
            statusLabel.imageColor = "success";
            statusLabel.paddingRight = 0;
        } else if (statusLabel.text === "Printed") {
            statusLabel.imageName = ImageName.printed;
            statusLabel.imageColor = "primary";
            statusLabel.paddingRight = 0;
        }
    }
}

export function setIcons(tableRow: TableRow, imageId: String, panelId: String, isEnabled: boolean,
    isTriumphPay: boolean = false) {
    const image = tableRow.findComponentById(imageId) as Image;
    const panel = tableRow.findComponentById(panelId) as Panel;
    if (image) {
        image.visible = isEnabled;
        if (isTriumphPay) {
            image.tooltip = "Triumph Quick Pay";
        }
    }
    if (panel) {
        panel.marginLeft = !isEnabled ? 30 : 0;
    }
}

export function createCommentModelRows(rows: any[]): ModelRow[] {
    return rows.map(row => {
        const modelRow = new ModelRow("lme/ar/billing-comments", true, row);
        modelRow.set("comment_text", row.comment_text);
        modelRow.set("comment_entered_by", row.comment_entered_by);
        return modelRow;
    });
}

export function readyToProcessOnClick(table: Table): void {
    const selectedBillingRows: ModelRow[] = [];
    const user = LMEUserSettings.getUserId();
    let okToProceed: boolean = true;
    let orderList = "";
    const selected = table?.rows?.filter(tableRow => {
        return (tableRow.data.getBoolean("selected", false));
    }).map((tableRow) => {
        const modelRow = tableRow.data as ModelRow;
        if (modelRow.get("invoice_exceptions").length > 0) {
            if (orderList) {
                orderList += ", ";
            }
            orderList += modelRow.get("order_id");
            okToProceed = false;
        } else {
            selectedBillingRows.push(tableRow.data as ModelRow);
        }
    });

    if (okToProceed) {
        CommonDialogs.showYesNo(selected.length + " invoices will be marked as ready to process. Do you want to continue?").then((response) => {
            if (response) {
                Api.post("lme/ar/mark-ready-to-process", { invoices_to_process: selectedBillingRows, billing_user_id: user }).then((response) => {
                    const success = response.data[0]?.success;
                    const message = response.data[0]?.message;
                    if (success) {
                        Toast.showSuccessToast(message);
                    } else {
                        Snackbar.showWarningSnackbar(message);
                    }
                });
            }
        });
    } else {
        //We shouldn't get here now with the way we're handling the button enable/disable but left it in 'just in case'
        Snackbar.showWarningSnackbar("Order(s) [" + orderList + "] with exceptions cannot be marked as 'Ready to Process'.  " +
            "Canceling operation.");
    }
}

export function setReadyToPrintButton(table: Table, button: Button, defaultValue: boolean): void {

    if (defaultValue) {
        const functionEnable = (tableRow: TableRow) => {
            return tableRow.data.get("printed")
                || tableRow.data.get("invoice_exceptions").length > 0;
        }
        const isEnabled = table?.rows?.filter(tableRow => {
            return (tableRow.data.getBoolean("selected", false));
        }).some(functionEnable);

        button.enabled = !isEnabled;
    } else {
        button.enabled = false;
    }
}

export function evaluateSelectedForCancel(table: Table, button: Button) {
    try {
        const selected = table?.rows?.filter(tableRow => {
            return (tableRow.data.getBoolean("selected", false));
        }).map((tableRow) => {
            return {
                total_charges: tableRow.data.get("total_charges"),
            }
        });
        if (selected.length > 0) {
            selected.forEach((row) => {
                if (row.total_charges.amount > 0) {
                    button.enabled = false;
                }
            });
        }
    } catch (error) {
        Snackbar.showWarningSnackbar("Error evaluating selected rows for cancellation: " + error.message);
    }
}

export function setExceptions(exceptionPanel: Panel, modelRow: ModelRow, printed: boolean) {
    const exceptions = modelRow.get("invoice_exceptions");
    const exceptionIcon = exceptionPanel?.findComponentById("imageException") as Image;
    const exceptionText = exceptionPanel?.findComponentById("labelExceptionText") as Label;
    const totalExceptionCount = exceptions.length + (printed ? 1 : 0);
    const textboxPropsArray: Partial<TextboxProps>[] = [totalExceptionCount];
    exceptionPanel.visible = true;

    if (exceptions.length === 1 && !printed) {
        exceptionIcon.color = "error";
        exceptionIcon.name = ImageName.circleErrorSolid;
        exceptionIcon.visible = true;
        exceptionText.text = exceptions[0]["exception_description"];
    } else {
        for (let i = 0; i < exceptions.length; i++) {
            const currException = exceptions[i];
            textboxPropsArray[i] = {
                caption: "Invoice Exception",
                text: currException["exception_description"],
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            }
        }

        if (printed) {
            textboxPropsArray[exceptions.length + 1] = {
                caption: "Status",
                text: "Invoice has been printed",
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            }
        }

        if (totalExceptionCount > 1) {
            exceptionText.text = `(${totalExceptionCount})`;
        }

        CommonTooltips.setTooltipContentTextboxes(exceptionIcon, () => textboxPropsArray);
    }
}

/**
 * Sets smart payee tooltips that check payee count only when tooltip is actually needed
 * This avoids API calls during initial row display and only makes them when user hovers
 */
export function setSmartPayeeTooltips(labelFullDriverName: Label, labelPayeecheckName: Label, modelRow: ModelRow): void {
    // Create a shared cache for the payee data for this order
    let payeeDataCache: any = null;
    let payeeDataPromise: Promise<any> | null = null;

    const getPayeeData = async (): Promise<any> => {
        // Return cached data if available
        if (payeeDataCache !== null) {
            return payeeDataCache;
        }

        // Return existing promise if one is in flight
        if (payeeDataPromise) {
            return payeeDataPromise;
        }

        // Make the API call and cache the promise
        payeeDataPromise = Api.post("lme/ar/payee-summary", { order_id: modelRow.get("order_id") })
            .then((response) => {
                if (response && response.data && response.data.length > 0 && response.data[0].payees) {
                    payeeDataCache = response.data[0].payees;
                    return payeeDataCache;
                }
                payeeDataCache = []; // Cache empty result to avoid repeated calls
                return payeeDataCache;
            })
            .catch((error) => {
                console.error("Error checking payee count:", error);
                payeeDataCache = []; // Cache empty result on error
                return payeeDataCache;
            })
            .finally(() => {
                payeeDataPromise = null; // Clear the promise reference
            });

        return payeeDataPromise;
    };

    // Set up smart individual tooltips that check payee count before showing
    if (labelFullDriverName) {
        labelFullDriverName.tooltipDynamicContent = async () => {
            const payeeData = await getPayeeData();

            // Only show individual tooltip if there's exactly one payee
            if (payeeData && payeeData.length === 1) {
                const driverId = modelRow.get("driver_id");
                if (driverId) {
                    const layout = await Layout.getLoadedLayout("lme/dispatch/asset/DriverQuickInfo");
                    // Try multiple possible data source names
                    const dataSource = (layout as any).sourceDriver || (layout as any).sourceEmployeeDriver || (layout as any).mainDataSource || (layout as any).dataSource;
                    if (dataSource && dataSource.search) {
                        await dataSource.search({ id: driverId });
                        return layout;
                    }
                }
            }

            // For multiple payees or no payees, return null (no tooltip)
            return null;
        };
    }

    if (labelPayeecheckName) {
        labelPayeecheckName.tooltipDynamicContent = async () => {
            const payeeData = await getPayeeData();

            // Only show individual tooltip if there's exactly one payee
            if (payeeData && payeeData.length === 1) {
                const payeeId = modelRow.get("payee_id");
                if (payeeId) {
                    const layout = await Layout.getLoadedLayout("lme/powerbroker/CarrierQuickInfo");
                    // Try multiple possible data source names
                    const dataSource = (layout as any).sourceCarrier || (layout as any).sourceCarrierInfo || (layout as any).mainDataSource || (layout as any).dataSource;
                    if (dataSource && dataSource.search) {
                        await dataSource.search({ id: payeeId });
                        return layout;
                    }
                }
            }

            // For multiple payees or no payees, return null (no tooltip)
            return null;
        };
    }
}

export function setTooltipContent(component: Component, modelRow: ModelRow, isAsset?: boolean) {
    if (component === null || component === undefined || component.id === null) return;
    switch (component.id) {
        case "labelBillingcommodityDescr":
            CommonTooltips.setTooltipContentLayout(component, modelRow.get("commodity_id"), "lme/dispatch/CommodityQuickInfo");
            component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
            break;
        case "panelOrigLocation":
            CommonTooltips.setTooltipContentLayout(component, modelRow.get("origin_location_id"), "lme/dispatch/LocationQuickInfo");
            component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
            break;
        case "panelDestLocation":
            CommonTooltips.setTooltipContentLayout(component, modelRow.get("destination_location_id"), "lme/dispatch/LocationQuickInfo");
            component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
            break;
        case "panelCustomer":
            CommonTooltips.setTooltipContentLayout(component, modelRow.get("customer_id"), "lme/ar/CustomerQuickInfo");
            break;
        case "panelControllingParty":
            CommonTooltips.setTooltipContentLayout(component, modelRow.get("controlling_party"), "lme/dispatch/LocationQuickInfo");
            component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
            break;
        case "labelFullDriverName":
            CommonTooltips.setTooltipContentLayout(component, modelRow.get("driver_id"), "lme/dispatch/asset/DriverQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
            break;
        case "labelPayeecheckName":
            CommonTooltips.setTooltipContentLayout(component, modelRow.get("payee_id"), "lme/powerbroker/CarrierQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
            break;
        case "labelFactoringCompanyname":
            component.tooltipDynamicContent = async () => {
                try {
                    const response = await Api.post("lme/ar/factoring-company", { order_id: modelRow.get("order_id") });

                    if (response && response.data && response.data.length > 0) {
                        const brokerageMovementCount = response.data[0].brokerage_movement_count;
                        const factoringCompanies = response.data[0].factoring_companies;

                        if (brokerageMovementCount >= 2 && factoringCompanies && factoringCompanies.length > 1) {
                            const layout = await Layout.getLoadedLayout("lme/ar/FactoringCompanySummaryQuickInfo");

                            // Create model rows for the table
                            const factoringCompanyModelRows = factoringCompanies.map(company => {
                                const row = new ModelRow("lme/ar/factoring-company", true, company);
                                row.set("id", company.id || "");
                                row.set("name", company.name || "");
                                row.set("city", company.city || "");
                                row.set("state", company.state || "");
                                row.set("zip_code", company.zip_code || "");
                                row.set("phone_number", company.phone_number || "");
                                return row;
                            });

                            const table = layout.findComponentById("tableFactoringCompanies");
                            if (table && table.dataSource) {
                                table.dataSource.setRowsAndMode(DataSourceMode.NONE, factoringCompanyModelRows);
                            }

                            // Populate the selected factoring company data source with the first company
                            const sourceSelectedFactoringCompany = (layout as any).sourceSelectedFactoringCompany;
                            if (sourceSelectedFactoringCompany && factoringCompanyModelRows.length > 0) {
                                sourceSelectedFactoringCompany.setRowsAndMode(DataSourceMode.NONE, [factoringCompanyModelRows[0]]);
                            }

                            // Set up table row hover functionality for detail panel updates
                            if (table && factoringCompanies.length > 1) {
                                const tableComponent = table as Table;

                                // Function to update detail panel with specific factoring company data
                                const updateDetailPanelWithCompany = (company: any) => {
                                    // Create a new ModelRow for this company and set it on the selectedFactoringCompany data source
                                    const selectedCompanyRow = new ModelRow("lme/ar/factoring-company", true, company);
                                    selectedCompanyRow.set("id", company.id || "");
                                    selectedCompanyRow.set("name", company.name || "");
                                    selectedCompanyRow.set("city", company.city || "");
                                    selectedCompanyRow.set("state", company.state || "");
                                    selectedCompanyRow.set("zip_code", company.zip_code || "");
                                    selectedCompanyRow.set("phone_number", company.phone_number || "");

                                    // Set this as the active row for the selected factoring company data source
                                    if (sourceSelectedFactoringCompany) {
                                        sourceSelectedFactoringCompany.setRowsAndMode(DataSourceMode.NONE, [selectedCompanyRow]);
                                        sourceSelectedFactoringCompany.displayDataInBoundComponents();
                                    }
                                };

                                const tableElement = tableComponent.element;
                                if (tableElement) {
                                    let currentHoveredRow = -1;

                                    tableElement.addEventListener('mousemove', (event) => {
                                        // Calculate which row the mouse is over based on position
                                        const tableRect = tableElement.getBoundingClientRect();
                                        const mouseY = event.clientY - tableRect.top;

                                        // Estimate row height (assuming uniform row height)
                                        const estimatedRowHeight = tableRect.height / (factoringCompanies.length + 1); // +1 for header
                                        const headerHeight = estimatedRowHeight; // First row is header

                                        if (mouseY > headerHeight) {
                                            const rowIndex = Math.floor((mouseY - headerHeight) / estimatedRowHeight);

                                            if (rowIndex >= 0 && rowIndex < factoringCompanies.length && rowIndex !== currentHoveredRow) {
                                                currentHoveredRow = rowIndex;

                                                const hoveredCompany = factoringCompanies[rowIndex];
                                                if (hoveredCompany) {
                                                    updateDetailPanelWithCompany(hoveredCompany);
                                                }
                                            }
                                        }
                                    });

                                    tableElement.addEventListener('click', (event) => {
                                        const tableRect = tableElement.getBoundingClientRect();
                                        const mouseY = event.clientY - tableRect.top;

                                        const estimatedRowHeight = tableRect.height / (factoringCompanies.length + 1);
                                        const headerHeight = estimatedRowHeight;

                                        if (mouseY > headerHeight) {
                                            const rowIndex = Math.floor((mouseY - headerHeight) / estimatedRowHeight);

                                            if (rowIndex >= 0 && rowIndex < factoringCompanies.length) {
                                                const clickedCompany = factoringCompanies[rowIndex];
                                                if (clickedCompany) {
                                                    updateDetailPanelWithCompany(clickedCompany);
                                                }
                                            }
                                        }
                                    });
                                }
                            }

                            return layout;
                        } else {
                            // For single factoring company or asset movements, use the existing single quick info
                            CommonTooltips.setTooltipContentLayout(component, modelRow.get("factoring_company_id"), "lme/settlement/FactoringCompanyQuickInfo", {
                                minWidth: 128,
                                minHeight: 140
                            });
                            return null;
                        }
                    }
                } catch (error) {
                    console.error("FactoringCompanySummaryQuickInfo: Error fetching factoring company data:", error);
                }

                // Return null if no data or error - default single tooltip will be used
                return null;
            };
            component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
            break;
        case "panelReferenceNum":
            CommonTooltips.setTooltipContentTextboxes(component, () => [
                { caption: "Bill of Lading", displayType: DisplayType.STRING, text: modelRow.get("bill_of_lading"), rowBreak: true, fillRow: true },
                { caption: "Consignee Reference Number", displayType: DisplayType.STRING, text: modelRow.get("consignee_ref_no"), rowBreak: false, fillRow: true }
            ]);
            break;
        case "panelComments":
            component.tooltipDynamicContent = async () => {
                const commentLayout = await Layout.getLoadedLayout("lme/ar/BillingCommentsQuickInfo") as BillingCommentsQuickInfo;
                await commentLayout.sourceBillingComments.search({ order_id: modelRow.get("order_id"), customer_id: modelRow.get("customer_id") });
                const intRows = commentLayout.sourceBillingComments.data[0].get("int_bill_comments");
                const extRows = commentLayout.sourceBillingComments.data[0].get("ext_bill_comments");

                if (commentLayout.sourceBillingComments.data[0].get("int_bill_comments").length === 0 &&
                    commentLayout.sourceBillingComments.data[0].get("ext_bill_comments").length === 0) {
                    return null;
                }
                if (intRows.length > 0) {
                    const intCommentLayout = commentLayout.layoutIntBillCommentsQuickInfo as IntBillCommentsQuickInfo;
                    const intCommentTable = intCommentLayout.tableIntBillComments;
                    const intCommentModelRows = createCommentModelRows(intRows);
                    intCommentLayout.tableIntBillComments.dataSource.setRowsAndMode(DataSourceMode.NONE, intCommentModelRows);
                } else {
                    commentLayout.layoutIntBillCommentsQuickInfo.visible = false;
                }

                if (extRows.length > 0) {
                    const extCommentLayout = commentLayout.layoutExtBillCommentsQuickInfo as ExtBillCommentsQuickInfo;
                    const extCommentTable = extCommentLayout.tableExtBillComments;
                    const extCommentModelRows = createCommentModelRows(extRows);
                    extCommentLayout.tableExtBillComments.dataSource.setRowsAndMode(DataSourceMode.NONE, extCommentModelRows);
                } else {
                    commentLayout.layoutExtBillCommentsQuickInfo.visible = false;
                }
                return commentLayout;
            }
            break;
        case "labelAutoRate":
        case "labelBillingtotalCharges":
            component.tooltipDynamicContent = async () => {
                const layout = await Layout.getLoadedLayout("lme/ar/InvoiceProcessingChargesQuickInfo") as InvoiceProcessingChargesQuickInfo;
                await layout.sourceBilling.search({ id: modelRow.get("billing_id") });
                await layout.sourceOtherChargeBill.search({ invoice_id: modelRow.get("billing_id") });
                if (!isAsset) {
                    await layout.sourceInvoiceMargins.search({ order_id: modelRow.get("order_id") });
                }
                return layout;
            };
            break;
        case "labelAgentName":
            CommonTooltips.setTooltipContentTextboxes(component, () => [
                { caption: "Agent ID", displayType: DisplayType.STRING, text: modelRow.get("agent_id"), rowBreak: true, fillRow: true },
                { caption: "Agent Name", displayType: DisplayType.STRING, text: modelRow.get("agent_name"), rowBreak: false, fillRow: true }
            ]);
            break;
        case "panelWeight":
            CommonTooltips.setTooltipContentTextboxes(component, () => [
                { caption: "Weight", displayType: DisplayType.STRING, text: setWeight(modelRow), rowBreak: true, fillRow: true },
                { caption: "Pieces", displayType: DisplayType.STRING, text: setPieces(modelRow), rowBreak: false, fillRow: true }
            ]);
            break;
        case "labelCarrierDriver":
            CommonTooltips.setTooltipContentTextboxes(component, () => [
                { caption: "Carrier Driver", displayType: DisplayType.STRING, text: modelRow.get("carrier_driver"), rowBreak: true, fillRow: true }
            ]);
            break;
        case "panelOnHold":
            component.tooltipDynamicContent = async () => {
                const layout = await Layout.getLoadedLayout("lme/ar/OnHoldInvoiceReasonCodeQuickInfo") as OnHoldInvoiceReasonCodeQuickInfo;
                layout.reasonText = modelRow.get("hold_reason_text");
                await layout.sourceBackOfficeReasonCode.search({ id: modelRow.get("hold_reason_code") });
                return layout;
            };
            break;
        case "panelAsset":
        case "panelBrokerage":
            component.tooltipDynamicContent = async () => {
                try {
                    const response = await Api.post("lme/ar/payee-summary", { order_id: modelRow.get("order_id") });
                    const orderId = modelRow.get("order_id");

                    if (response && response.data && response.data.length > 0 && response.data[0].payees) {
                        const payeeData = response.data[0].payees;

                        if (payeeData && payeeData.length > 1) {
                            const layout = await Layout.getLoadedLayout("lme/ar/PayeeSummaryQuickInfo");                            // Create model rows for the table
                            const payeeModelRows = payeeData.map(payee => {
                                const row = new ModelRow("lme/ar/payee-summary", true, payee);
                                row.set("type", payee.type || "");
                                row.set("name", payee.name || "");
                                row.set("formattedAddress", payee.formattedAddress || "");
                                row.set("payeeId", payee.payeeId || "");
                                row.set("phone", payee.phone || "");
                                row.set("movementId", payee.movementId || "");
                                row.set("email", payee.email || "");
                                row.set("fleetManager", payee.fleetManager || "");
                                row.set("dotNumber", payee.dotNumber || "");
                                row.set("carrier_ranking", payee.starCount || 0);
                                row.set("isDriver", payee.isDriver || false);
                                row.set("hasSplitDrop", payee.hasSplitDrop || "N");
                                row.set("hasSplitPick", payee.hasSplitPick || "N");
                                return row;
                            });

                            const table = layout.findComponentById("tablePayees") as Table;
                            if (table && table.dataSource) {
                                table.dataSource.setRowsAndMode(DataSourceMode.NONE, payeeModelRows);

                                // Adjust the first column width to accommodate split images and type text
                                if (table.columns && table.columns.length > 0) {
                                    const firstColumn = table.columns[0];
                                    if (firstColumn && firstColumn.headingCell) {
                                        firstColumn.headingCell.fixedWidth = "40%";  // Increase from 25% to 35%
                                        // Also set width for the cell definition
                                        if (firstColumn.cellDef && firstColumn.cellDef.cellProps) {
                                            firstColumn.cellDef.cellProps.fixedWidth = "40%";
                                        }
                                    }

                                    // Adjust the second column accordingly
                                    const secondColumn = table.columns[1];
                                    if (secondColumn && secondColumn.headingCell) {
                                        secondColumn.headingCell.fixedWidth = "60%";  // Decrease from 75% to 65%
                                        // Also set width for the cell definition
                                        if (secondColumn.cellDef && secondColumn.cellDef.cellProps) {
                                            secondColumn.cellDef.cellProps.fixedWidth = "60%";
                                        }
                                    }
                                }

                                // Set up row display listener to control image visibility
                                table.addRowDisplayListener((event: any) => {
                                    const tableRow = event.getTableRow();
                                    const modelRow = tableRow.data;

                                    const imageYSplitDrop = tableRow.findComponentById("imageYSplitDrop") as Image;
                                    const imageYSplitPick = tableRow.findComponentById("imageYSplitPick") as Image;

                                    if (imageYSplitDrop) {
                                        imageYSplitDrop.visible = modelRow.get("hasSplitDrop") === "Y";
                                    }

                                    if (imageYSplitPick) {
                                        imageYSplitPick.visible = modelRow.get("hasSplitPick") === "Y";
                                    }
                                });
                            }

                            // Also populate the selected payee data source with the first payee
                            const sourceSelectedPayee = (layout as any).sourceSelectedPayee;
                            if (sourceSelectedPayee && payeeModelRows.length > 0) {
                                sourceSelectedPayee.setRowsAndMode(DataSourceMode.NONE, [payeeModelRows[0]]);
                            }

                            const firstPayee = payeeData[0];
                            const textboxId = layout.findComponentById("textboxId") as any;
                            const textboxName = layout.findComponentById("textboxName") as any;
                            const textboxAddress = layout.findComponentById("textboxAddress") as any;
                            const textboxContactphone = layout.findComponentById("textboxContactphone") as any;
                            const textboxMovementId = layout.findComponentById("textboxMovementId") as any;
                            const textboxEmail = layout.findComponentById("textboxEmail") as any;
                            const textboxFleetManager = layout.findComponentById("textboxFleetManager") as any;
                            const textboxIccNumber = layout.findComponentById("textboxIccNumber") as any;
                            const textboxDotNumber = layout.findComponentById("textboxDotNumber") as any;
                            const starCount = layout.findComponentById("starratingC") as StarRating;

                            if (textboxId) {
                                textboxId.text = firstPayee.payeeId || "";
                                if (firstPayee.payeeId) {
                                    textboxId.addUnderlineWhenMouseOver();
                                    textboxId.addClickListener((event) => {
                                        const currentPayeeId = textboxId.text;
                                        if (currentPayeeId) {
                                            DrsPayee.navigateTo(currentPayeeId);
                                        }
                                    });
                                }
                            }
                            if (textboxName) textboxName.text = firstPayee.name || "";
                            if (textboxAddress) textboxAddress.text = firstPayee.formattedAddress || "";
                            if (textboxContactphone) {
                                textboxContactphone.text = firstPayee.phone || "";
                                textboxContactphone.visible = !!(firstPayee.phone && firstPayee.phone.trim());
                            }
                            if (textboxMovementId) {
                                textboxMovementId.text = firstPayee.movementId || "";
                                if (firstPayee.movementId) {
                                    textboxMovementId.addUnderlineWhenMouseOver();
                                    textboxMovementId.addClickListener((event) => {
                                        const currentMovementId = textboxMovementId.text;
                                        if (currentMovementId) {
                                            //Movement.navigateTo(currentMovementId);
                                            OrderTabView.navigateTo({key: currentMovementId, orderId:orderId, additionalProps: {showVoid: false}, newTab: true});
                                        }
                                    });
                                }
                            }
                            if (textboxEmail) {
                                textboxEmail.text = firstPayee.email || "";
                                textboxEmail.displayType = DisplayType.EMAIL;
                                textboxEmail.visible = !!(firstPayee.email && firstPayee.email.trim());
                            }

                            if(firstPayee.isDriver) {
                                if (textboxFleetManager) textboxFleetManager.text = firstPayee.fleetManager || "";
                                textboxIccNumber.visible = false;
                                textboxDotNumber.visible = false;
                                starCount.visible = false;
                                textboxFleetManager.visible = true;
                            } else {
                                if (textboxIccNumber) textboxIccNumber.text = firstPayee.iccNumber || "";
                                if (textboxDotNumber) textboxDotNumber.text = firstPayee.dotNumber || "";
                                if (starCount) starCount.value = firstPayee.starCount || 0;

                                textboxFleetManager.visible = false;
                                textboxIccNumber.visible = true;
                                textboxDotNumber.visible = true;
                                starCount.visible = true;
                            }

                            // Set up table row hover functionality for detail panel updates
                            if (table && payeeData.length > 1) {
                                const tableComponent = table as Table;

                                // Function to update detail panel with specific payee data
                                const updateDetailPanelWithPayee = (payee: any) => {
                                    // Create a new ModelRow for this payee and set it on the selectedPayee data source
                                    const selectedPayeeRow = new ModelRow("lme/ar/payee-summary", true, payee);
                                    selectedPayeeRow.set("type", payee.type || "");
                                    selectedPayeeRow.set("name", payee.name || "");
                                    selectedPayeeRow.set("formattedAddress", payee.formattedAddress || "");
                                    selectedPayeeRow.set("payeeId", payee.payeeId || "");
                                    selectedPayeeRow.set("phone", payee.phone || "");
                                    selectedPayeeRow.set("movementId", payee.movementId || "");
                                    selectedPayeeRow.set("email", payee.email || "");
                                    selectedPayeeRow.set("fleetManager", payee.fleetManager || "");
                                    selectedPayeeRow.set("dotNumber", payee.dotNumber || "");
                                    selectedPayeeRow.set("icc_number", payee.iccNumber || "");
                                    selectedPayeeRow.set("dot_number", payee.dotNumber || "");
                                    selectedPayeeRow.set("fleet_manager", payee.fleetManager || "");
                                    selectedPayeeRow.set("carrier_ranking", payee.starCount || 0);
                                    selectedPayeeRow.set("isDriver", payee.isDriver || false);
                                    selectedPayeeRow.set("hasSplitDrop", payee.hasSplitDrop || "N");
                                    selectedPayeeRow.set("hasSplitPick", payee.hasSplitPick || "N");

                                    // Set this as the active row for the selected payee data source
                                    if (sourceSelectedPayee) {
                                        sourceSelectedPayee.setRowsAndMode(DataSourceMode.NONE, [selectedPayeeRow]);
                                        sourceSelectedPayee.displayDataInBoundComponents();
                                    }

                                    // Also manually update the textboxes for immediate feedback
                                    if (textboxId) {
                                        textboxId.text = payee.payeeId || "";
                                        if (payee.payeeId) {
                                            textboxId.addUnderlineWhenMouseOver();
                                            textboxId.addClickListener((event) => {
                                                const currentPayeeId = textboxId.text;
                                                if (currentPayeeId) {
                                                    DrsPayee.navigateTo(currentPayeeId);
                                                }
                                            });
                                        }
                                    }
                                    if (textboxName) {
                                        textboxName.text = payee.name || "";
                                    }
                                    if (textboxAddress) {
                                        textboxAddress.text = payee.formattedAddress || "";
                                    }
                                    if (textboxContactphone) {
                                        textboxContactphone.text = payee.phone || "";
                                        textboxContactphone.visible = !!(payee.phone && payee.phone.trim());
                                    }
                                    if (textboxMovementId) {
                                        textboxMovementId.text = payee.movementId || "";
                                        if (payee.movementId) {
                                            textboxMovementId.addUnderlineWhenMouseOver();
                                            textboxMovementId.addClickListener((event) => {
                                                const currentMovementId = textboxMovementId.text;
                                                if (currentMovementId) {
                                                    // Movement.navigateTo(currentMovementId);
                                                    OrderTabView.navigateTo({key: currentMovementId, orderId:orderId, additionalProps: {showVoid: false}, newTab: true});
                                                }
                                            });
                                        }
                                    }
                                    if (textboxEmail) {
                                        textboxEmail.text = payee.email || "";
                                        textboxEmail.displayType = DisplayType.EMAIL;
                                        textboxEmail.visible = !!(payee.email && payee.email.trim());
                                    }

                                    if (payee.isDriver === true) {
                                        if (textboxFleetManager) textboxFleetManager.text = payee.fleetManager || "";
                                        textboxIccNumber.visible = false;
                                        textboxDotNumber.visible = false;
                                        starCount.visible = false;
                                        textboxFleetManager.visible = true;
                                    } else {
                                        if (textboxIccNumber) textboxIccNumber.text = payee.iccNumber || "";
                                        if (textboxDotNumber) textboxDotNumber.text = payee.dotNumber || "";
                                        textboxFleetManager.visible = false;
                                        textboxIccNumber.visible = true;
                                        textboxDotNumber.visible = true;
                                        starCount.visible = true;
                                    }
                                };

                                if (tableComponent.selectionMode !== undefined) {
                                    // Listen for selection changes - fix the parameters
                                    const originalSelectRow = (tableComponent as any).selectRow;
                                    if (originalSelectRow) {
                                        (tableComponent as any).selectRow = function(rowIndex: number, selected: boolean) {
                                            if (selected && rowIndex >= 0 && rowIndex < tableComponent.rows.length) {
                                                const tableRow = tableComponent.rows[rowIndex];

                                                if (tableRow && tableRow.data) {
                                                    const rowData = tableRow.data as ModelRow;
                                                    const payeeId = rowData.get("payeeId");

                                                    const matchingPayee = payeeData.find(p => p.payeeId === payeeId);
                                                    if (matchingPayee) {
                                                        updateDetailPanelWithPayee(matchingPayee);
                                                    }
                                                }
                                            }

                                            // Call original function
                                            if (originalSelectRow) {
                                                originalSelectRow.call(this, rowIndex, selected);
                                            }
                                        };
                                    }

                                    const tableElement = tableComponent.element;
                                    if (tableElement) {
                                        let currentHoveredRow = -1;

                                        tableElement.addEventListener('mousemove', (event) => {
                                            // Calculate which row the mouse is over based on position
                                            const tableRect = tableElement.getBoundingClientRect();
                                            const mouseY = event.clientY - tableRect.top;

                                            // Estimate row height (assuming uniform row height)
                                            const estimatedRowHeight = tableRect.height / (payeeData.length + 1); // +1 for header
                                            const headerHeight = estimatedRowHeight; // First row is header

                                            if (mouseY > headerHeight) {
                                                const rowIndex = Math.floor((mouseY - headerHeight) / estimatedRowHeight);

                                                if (rowIndex >= 0 && rowIndex < payeeData.length && rowIndex !== currentHoveredRow) {
                                                    currentHoveredRow = rowIndex;

                                                    const hoveredPayee = payeeData[rowIndex];
                                                    if (hoveredPayee) {
                                                        updateDetailPanelWithPayee(hoveredPayee);
                                                    }
                                                }
                                            }
                                        });

                                        tableElement.addEventListener('click', (event) => {
                                            const tableRect = tableElement.getBoundingClientRect();
                                            const mouseY = event.clientY - tableRect.top;

                                            const estimatedRowHeight = tableRect.height / (payeeData.length + 1);
                                            const headerHeight = estimatedRowHeight;

                                            if (mouseY > headerHeight) {
                                                const rowIndex = Math.floor((mouseY - headerHeight) / estimatedRowHeight);

                                                if (rowIndex >= 0 && rowIndex < payeeData.length) {
                                                    const clickedPayee = payeeData[rowIndex];
                                                    if (clickedPayee) {
                                                        updateDetailPanelWithPayee(clickedPayee);
                                                    }
                                                }
                                            }
                                        });
                                    }
                                }

                                // Fallback: Add click listener to the entire table element (less precise)
                                const tableElement = tableComponent.element;
                                if (tableElement) {
                                    tableElement.addEventListener('click', (event) => {
                                         // Only use this as fallback if no direct row click was detected
                                        // This is less precise but provides backup functionality
                                    });
                                }
                            }

                            return layout;
                        } else {
                            // For single payees, don't use PayeeSummaryQuickInfo - let individual tooltips handle it
                            return null;
                        }
                    }
                } catch (error) {
                    console.error("PayeeSummaryQuickInfo: Error fetching payee data:", error);
                }

                // Return null if no data or error - individual tooltips will be used
                return null;
            };
            component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
            break;
    }
}

export function updateFilterCounts(originalFilters: SecondaryFilter[], type: InvoiceProcessingTableType, filterPanel: FilterPanel, dataSource: DataSource, refreshDynamicFilters: boolean) {
    // Check if panel is mounted and avoid concurrent updates (like standard FilterPanel)
    if (!filterPanel.panel.isMounted) {
        return;
    }

    // Handle dynamic filters first
    if (refreshDynamicFilters) {
        updateDynamicFilters(originalFilters, filterPanel, dataSource);
    }

    // Calculate new counts for all filters
    const newCounts = calculateFilterCounts(originalFilters, type, dataSource);

    // Check if any counts have actually changed before updating UI
    if (!hasFilterCountChanges(originalFilters, newCounts)) {
        return;
    }

    // Update filter UI with new counts
    updateFilterUI(originalFilters, newCounts);

    // Handle dynamic filter visibility (top 10 only)
    updateDynamicFilterVisibility(originalFilters);

    // Clear active filter if it has zero count
    clearInactiveFilter(filterPanel);
}

/**
 * Updates dynamic filters by adding new ones and updating existing captions
 */
function updateDynamicFilters(originalFilters: SecondaryFilter[], filterPanel: FilterPanel, dataSource: DataSource) {
    const dynamicFilters = refreshDynamicTaskFilters(filterPanel, dataSource);
    for (const filter of dynamicFilters) {
        const original = originalFilters.find(record => record.id === filter.id);
        if (!original) {
            originalFilters.push(filter);
            filterPanel.addFilter(filter);
        } else if (original.caption !== filter.caption) {
            original.caption = filter.caption;
        }
    }
}

/**
 * Calculates new counts for all filters based on current data
 */
function calculateFilterCounts(filters: SecondaryFilter[], type: InvoiceProcessingTableType, dataSource: DataSource): Map<SecondaryFilter, number> {
    const counts = new Map<SecondaryFilter, number>();

    // Initialize all counts to 0
    filters.forEach(filter => counts.set(filter, 0));

    // Count matching rows for each filter
    const filteredRows = dataSource.data.filter(row => type.filter(row));
    for (const row of filteredRows) {
        for (const filter of filters) {
            if (filter.filter(row)) {
                const currentCount = counts.get(filter) ?? 0;
                counts.set(filter, currentCount + 1);
            }
        }
    }

    return counts;
}

/**
 * Checks if any filter counts have changed
 */
function hasFilterCountChanges(filters: SecondaryFilter[], newCounts: Map<SecondaryFilter, number>): boolean {
    return filters.some(filter => filter.count !== newCounts.get(filter));
}

/**
 * Updates the UI for all filters with new counts and captions
 */
function updateFilterUI(filters: SecondaryFilter[], newCounts: Map<SecondaryFilter, number>) {
    for (const filter of filters) {
        const newCount = newCounts.get(filter) ?? 0;
        const newCaption = `${filter.caption} ${newCount}`;
        const shouldBeVisible = newCount > 0;

        // Only update properties if they've actually changed to prevent flickering
        if (filter.count !== newCount) {
            filter.count = newCount;
        }

        if (filter.button) {
            if (filter.button.caption !== newCaption) {
                filter.button.caption = newCaption;
            }
            if (filter.button.visible !== shouldBeVisible) {
                filter.button.visible = shouldBeVisible;
            }
        }
    }
}

/**
 * Updates visibility for dynamic filters to show only top 10
 */
function updateDynamicFilterVisibility(filters: SecondaryFilter[]) {
    const dynamicFilters = filters.filter(f => f.dynamic);
    if (dynamicFilters.length === 0) return;

    const top10Reasons = dynamicFilters
        .filter(f => f.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

    dynamicFilters.forEach(filter => {
        if (filter.button) {
            const shouldBeVisible = filter.count > 0 && top10Reasons.includes(filter);
            if (filter.button.visible !== shouldBeVisible) {
                filter.button.visible = shouldBeVisible;
            }
        }
    });
}

/**
 * Clears the active filter if it has zero count
 */
function clearInactiveFilter(filterPanel: FilterPanel) {
    const activeFilter = filterPanel.getActiveFilter();
    if (activeFilter && activeFilter.count === 0) {
        filterPanel.clearActiveFilter();
    }
}

export function refreshDynamicTaskFilters(filterPanel: FilterPanel, dataSource: DataSource): SecondaryFilter[] {
    const secondaryFilters: SecondaryFilter[] = []

    // Don't remove existing dynamic filters immediately - let updateFilterCounts handle visibility
    const existingDynamicFilters = filterPanel.filters.filter(f => f.dynamic);

    const uniqueReasonCodes: Map<string, string> = new Map();
    for (const modelRow of dataSource.data) {
        if (!StringUtil.isEmptyString(modelRow.get("hold_reason_code"))) {
            uniqueReasonCodes.set(modelRow.get("hold_reason_code"), modelRow.get("hold_reason_descr"));
        }
    }

    for (const [reasonCode, reasonDesc] of uniqueReasonCodes) {
        // Check if filter already exists
        const existingFilter = existingDynamicFilters.find(f => f.id === reasonCode);
        if (existingFilter) {
            // Update caption if changed
            existingFilter.caption = reasonDesc;
            secondaryFilters.push(existingFilter);
        } else {
            // Create new filter
            const filter: SecondaryFilterProps = {
                id: reasonCode,
                caption: reasonDesc,
                filter: (row: ModelRow) => {
                    return row.get("hold_reason_code") == reasonCode;
                },
                dynamic: true
            };
            // Add the filter to the panel (this adds it to the UI and the filters array)
            filterPanel.addFilter(filter);
            // Find the newly added filter to include in returned array
            const newFilter = filterPanel.filters.find(f => f.id === reasonCode);
            if (newFilter) {
                secondaryFilters.push(newFilter);
            }
        }
    }

    // Mark unused dynamic filters for removal (by setting count to 0)
    existingDynamicFilters.forEach(existingFilter => {
        if (!uniqueReasonCodes.has(existingFilter.id)) {
            existingFilter.count = 0;
            if (existingFilter.button && existingFilter.button.visible) {
                existingFilter.button.visible = false;
            }
        }
    });

    return secondaryFilters;
}

export function removeDynamicTaskFilters(filterPanel: FilterPanel): void {
    for (const filter of filterPanel.filters) {
        if (filter.dynamic) {
            filterPanel.removeFilterFromPanel(filter);
        }
    }
}

export function instanceOfInvoiceProcessingTable(object: any): object is InvoiceProcessingTable {
    return object.tableProcessing && object.filterPanel;
}

export class InvoiceProcessingTableListeners {
    static invoiceProcessingOnRowDisplayListener(tableRow: TableRow, modelRow: ModelRow) {
        const checkboxSingle = tableRow.findComponentById("checkboxSingle") as Checkbox;

        if (checkboxSingle) {
            checkboxSingle.printable = false;
        }
        setInvoiceStatusImage(tableRow);
        const labelStopsPickDrop = tableRow.findComponentById("labelStopsPickDrop") as Label;
        if (labelStopsPickDrop) {
            labelStopsPickDrop.applyLinkStyle();
        }

        const isFactoringCompany = modelRow.get("is_factoring_company");
        const panelFactoringCompany = tableRow.findComponentById("panelFactoringCompany");
        const panelBrokerage = tableRow.findComponentById("panelBrokerage") as Panel;
        const panelAsset = tableRow.findComponentById("panelAsset") as Panel;
        const isAsset = modelRow.get("default_move_type") === "A";
        const printed = modelRow.get("printed");
        if (panelBrokerage) {
            panelBrokerage.visible = !isAsset;
            if (!isAsset && isFactoringCompany) {
                if (panelFactoringCompany) {
                    panelFactoringCompany.visible = true;
                }
            }
        }

        if (panelAsset) {
            panelAsset.visible = isAsset;
        }
        const isCustomerId = tableRow.findComponentById("labelBillingcustomerId") as Label;
        if (isCustomerId) {
            const isCustomerBridgeIdValue = modelRow.get("is_customer_bridge_id");
            if (!isCustomerBridgeIdValue) {
                isCustomerId.imageName = null;
            } else {
                isCustomerId.imageProps = { paddingTop: 0, paddingBottom: 0, marginTop: 0, marginBottom: 0 };
            }
        }
        setIcons(tableRow, "imageSplitPay", "panelCustomer", modelRow.get("is_split_pay"));
        setIcons(tableRow, "imageQuickPay", "panelBrokerage", modelRow.get("is_quick_pay"), modelRow.get("is_triumph_pay_quick_pay"));
        const commodityDescr = modelRow.get("commodity_description");
        const commodityDescrTextbox = tableRow.findComponentById("labelBillingcommodityDescr") as Label;
        if (commodityDescrTextbox) {
            commodityDescrTextbox.visible = commodityDescr !== undefined && commodityDescr !== null;
        }

        const panelOrigLocation = tableRow.findComponentById("panelOrigLocation") as Panel;
        const panelDestLocation = tableRow.findComponentById("panelDestLocation") as Panel;
        const panelCustomer = tableRow.findComponentById("panelCustomer") as Panel;
        const panelControllingParty = tableRow.findComponentById("panelControllingParty") as Panel;
        const labelFullDriverName = tableRow.findComponentById("labelFullDriverName") as Label;
        const labelPayeecheckName = tableRow.findComponentById("labelPayeecheckName") as Label;
        const labelFactoringCompanyname = tableRow.findComponentById("labelFactoringCompanyname") as Label;
        const labelBillingtotalCharges = tableRow.findComponentById("labelBillingtotalCharges") as Label;
        const labelAgentName = tableRow.findComponentById("labelAgentName") as Label;
        const panelWeight = tableRow.findComponentById("panelWeight") as Panel;
        const panelComments = tableRow.findComponentById("panelComments") as Panel;
        const panelOnHold = tableRow.findComponentById("panelOnHold") as Panel;
        const labelAutoRate = tableRow.findComponentById("labelAutoRate") as Label;
        labelAutoRate.visible = modelRow.get("rate_id") > 0;

        updateExceptionsPanel(tableRow, modelRow, printed);
        setCommentsInfo(tableRow, modelRow.get("order_id"), modelRow.get("customer_id"));
        setTooltipContent(commodityDescrTextbox, modelRow);
        setTooltipContent(panelOrigLocation, modelRow);
        setTooltipContent(panelDestLocation, modelRow);
        setTooltipContent(panelCustomer, modelRow);
        setTooltipContent(panelControllingParty, modelRow);

        // Set smart payee tooltips that will determine which tooltip to use when actually needed
        setSmartPayeeTooltips(labelFullDriverName, labelPayeecheckName, modelRow);

        setTooltipContent(labelFactoringCompanyname, modelRow);
        setTooltipContent(labelAgentName, modelRow);
        setTooltipContent(panelWeight, modelRow);
        setTooltipContent(panelComments, modelRow);
        setTooltipContent(labelBillingtotalCharges, modelRow, isAsset);
        setTooltipContent(panelOnHold, modelRow);
        setTooltipContent(labelAutoRate, modelRow, isAsset);

        // Add tooltip for payee information on asset and brokerage panels
        setTooltipContent(panelAsset, modelRow);
        setTooltipContent(panelBrokerage, modelRow);

        const billOfLading = modelRow.get("bill_of_lading");
        const consigneeRefNo = modelRow.get("consignee_ref_no");
        if (billOfLading || consigneeRefNo) {
            const panelReferenceNum = tableRow.findComponentById("panelReferenceNum") as Label;
            setTooltipContent(panelReferenceNum, modelRow);
        }
        const carrierDriver = modelRow.get("carrier_driver");
        if (carrierDriver) {
            const labelCarrierDriver = tableRow.findComponentById("labelCarrierDriver") as Label;
            setTooltipContent(labelCarrierDriver, modelRow);
        }
        const weightLabel = tableRow.findComponentById("labelWeight") as Label;
        if (weightLabel) {
            weightLabel.text = setWeight(modelRow);
        }
        const piecesLabel = tableRow.findComponentById("labelPieces") as Label;
        if (piecesLabel) {
            piecesLabel.text = setPieces(modelRow);
        }
    }

    static onRowDisplayOrderCount(tableRow: TableRow, modelRow: ModelRow, table: Table) {
        const orderIdCount = tableRow.findComponentById("labelDupOrderID") as Label;
        if (orderIdCount) {
            const orderId = modelRow.get("order_id");
            let count = 0;
            if (!StringUtil.isEmptyString(orderId)) {
                count = table.rows.filter(row => row.data.get("order_id") === orderId)?.length;
                if (count <= 1) {
                    orderIdCount.visible = false;
                } else {
                    orderIdCount.visible = true;
                    orderIdCount.text = count as unknown as string;
                }
            }
            modelRow.set("order_count", count);
        }
    }
}
